import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialogue } from '../components/Dialogue';
import { GoalRow } from '../components/GoalRow';
import { LeafStorm } from '../components/LeafStorm';
import { MiniStat } from '../components/MiniStat';
import { Panel } from '../components/Panel';
import { PixelButton } from '../components/PixelButton';
import { StreakDot } from '../components/StreakDot';
import { useAddQuest } from '../lib/addQuest';
import { addDays, dayKey } from '../lib/date';
import { useStore } from '../lib/store';
import { currentStreak, hasCheckIn, longestStreak } from '../lib/streak';

export function TodayScreen() {
  const nav = useNavigate();
  const { openModal } = useAddQuest();
  const { goals, checkInIndex, today, toggleToday } = useStore();

  const habits = goals.filter(g => g.type === 'habit' && !g.archived_at);
  const todos = goals.filter(
    g =>
      g.type === 'todo' &&
      !g.archived_at &&
      !g.completed_at &&
      (!g.due_date || g.due_date <= today),
  );

  const now = new Date();
  // Count completed-today todos separately so totalToday and doneToday stay
  // symmetric. Without this, `todos` (filtered to !completed_at) shrinks as
  // todos are ticked, so totalToday shrinks while doneToday grows — and
  // `remaining` overshoots zero into negatives, never firing the all-done
  // dialogue or the finale storm.
  const habitsCheckedToday = habits.filter(g => hasCheckIn(checkInIndex, g.id, today)).length;
  const todosCompletedToday = goals.filter(
    g => g.type === 'todo' && g.completed_at && dayKey(new Date(g.completed_at)) === today,
  ).length;
  const totalToday = habits.length + todos.length + todosCompletedToday;
  const doneToday = habitsCheckedToday + todosCompletedToday;

  const longest = goals
    .filter(g => g.type === 'habit')
    .reduce((m, g) => Math.max(m, longestStreak(checkInIndex, g.id)), 0);

  const topStreak = habits
    .map(g => currentStreak(checkInIndex, g.id, now))
    .reduce((m, s) => Math.max(m, s), 0);

  const weekSoFar = countLastN(checkInIndex, habits.map(g => g.id), 7, now);
  const totalCheckIns = [...checkInIndex.values()].reduce((n, s) => n + s.size, 0);

  const onTheLine = habits
    .filter(g => !hasCheckIn(checkInIndex, g.id, today) && currentStreak(checkInIndex, g.id, addDays(now, -1)) > 0)
    .slice(0, 3);

  const remaining = totalToday - doneToday;

  const prevRemainingRef = useRef(remaining);
  const [stormId, setStormId] = useState<number | null>(null);

  useEffect(() => {
    if (
      prevRemainingRef.current > 0 &&
      remaining === 0 &&
      totalToday > 0
    ) {
      setStormId(Date.now());
    }
    prevRemainingRef.current = remaining;
  }, [remaining, totalToday]);

  useEffect(() => {
    if (stormId === null) return;
    const t = window.setTimeout(() => setStormId(null), 2200);
    return () => window.clearTimeout(t);
  }, [stormId]);

  return (
    <div className="grid-today">
      <Panel title={`TODAY · ${formatDayTitle(now)}`} bodyClassName="panel-body">
        {stormId !== null && <LeafStorm key={stormId} />}
        <Dialogue speaker="GOOD MORNING" style={{ marginBottom: 16 }}>
          {totalToday === 0 ? (
            <>No chores yet — plant a little seed to get started.</>
          ) : remaining === 0 ? (
            <>Every chore tended today. Rest well, friend.</>
          ) : (
            <>
              You have {remaining} chore{remaining === 1 ? '' : 's'} left to tend today.
              {onTheLine.length > 0 && (
                <>
                  {' '}
                  {onTheLine.length} streak{onTheLine.length === 1 ? '' : 's'} still waiting for a little love — small steps, kept.
                </>
              )}
            </>
          )}
        </Dialogue>

        <div className="section-label">— DAILY —</div>
        {habits.length === 0 && (
          <div style={{ padding: 16, color: '#6b3a1d', fontSize: 20 }}>
            No daily chores yet. + Plant a seed.
          </div>
        )}
        {habits.map(g => (
          <GoalRow
            key={g.id}
            goal={g}
            done={hasCheckIn(checkInIndex, g.id, today)}
            streak={currentStreak(checkInIndex, g.id, now)}
            onToggle={() => toggleToday(g)}
            onOpen={() => nav(`/quest/${g.id}`)}
          />
        ))}

        <div className="section-label" style={{ margin: '18px 0 8px' }}>— ONE-OFFS —</div>
        {todos.length === 0 && (
          <div style={{ padding: 16, color: '#6b3a1d', fontSize: 20 }}>No little errands waiting.</div>
        )}
        {todos.map(g => (
          <GoalRow
            key={g.id}
            goal={g}
            done={!!g.completed_at}
            streak={0}
            onToggle={() => toggleToday(g)}
            onOpen={() => nav(`/quest/${g.id}`)}
          />
        ))}
      </Panel>

      <div className="screens-col">
        <Panel title="PROGRESS">
          <div className="pixel-font" style={{ fontSize: 10, color: '#7a2a1f', marginBottom: 8 }}>
            {doneToday} / {totalToday || 0} TODAY
          </div>
          <div className="hp-bar">
            <span style={{ width: `${totalToday === 0 ? 0 : (doneToday / totalToday) * 100}%` }} />
          </div>
          <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <MiniStat label="STREAK" value={`${topStreak}d`} icon="dot" streak={topStreak} />
            <MiniStat label="LONGEST" value={`${longest}d`} icon="star" />
            <MiniStat label="THIS WK" value={`${weekSoFar}/${habits.length * 7 || 0}`} />
            <MiniStat label="TOTAL" value={String(totalCheckIns)} />
          </div>
        </Panel>

        <Panel title="STILL TO TEND">
          {onTheLine.length === 0 ? (
            <div style={{ fontFamily: 'VT323', fontSize: 18, color: '#6b3a1d' }}>
              All tended — or no streaks growing yet.
            </div>
          ) : (
            onTheLine.map(g => (
              <div
                key={g.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 4px',
                  borderBottom: '2px dotted rgba(107,58,29,.2)',
                }}
              >
                <StreakDot streak={currentStreak(checkInIndex, g.id, addDays(now, -1))} />
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontFamily: 'VT323',
                    fontSize: 18,
                  }}
                >
                  {g.title}
                </div>
                <span className="pixel-font" style={{ fontSize: 10, color: '#7a2a1f', flexShrink: 0 }}>
                  {currentStreak(checkInIndex, g.id, addDays(now, -1))}d
                </span>
              </div>
            ))
          )}
        </Panel>

        <PixelButton variant="gold" onClick={openModal}>+ PLANT A SEED</PixelButton>
      </div>
    </div>
  );
}

function countLastN(
  idx: Map<string, Set<string>>,
  ids: string[],
  n: number,
  today: Date,
): number {
  let c = 0;
  for (const id of ids) {
    const set = idx.get(id);
    if (!set) continue;
    for (let i = 0; i < n; i++) {
      if (set.has(dayKey(addDays(today, -i)))) c++;
    }
  }
  return c;
}

function formatDayTitle(d: Date): string {
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  return `${months[d.getMonth()]} ${d.getFullYear()} · DAY ${d.getDate()}`;
}
